import { useEffect, useState } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import Input from "../../form/input/InputField";
import Checkbox from "../../form/input/Checkbox";
import MultiSelectCheckbox from '../../form/MultiSelectCheckbox';
import Switch from "../../form/switch/Switch";
import Button from "../../ui/button/Button";
import { useNavigate } from "react-router";
import axios from "axios";
import { authService } from "../../../services/authService";

// Tipagem específica para os eventos
type WebhookEvent =
    | 'sale_approved'
    | 'sale_rejected'
    | 'sale_refunded'
    | 'sale_chargeback'
    | 'sale_abandoned'
    | 'sale_pix_generated'
    | 'sale_boleto_generated'
    | 'subscription_canceled'
    | 'subscription_overdue'
    | 'subscription_renewed';

interface WebhookFormData {
    active: boolean;
    name: string;
    webhookUrl: string;
    productList: string[];
    global: boolean;
    events: WebhookEvent[];
    type: 'global' | 'specific';
}

interface NewWebhookFormProps {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

export default function NewWebhookForm({ onSuccess, onError }: NewWebhookFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<WebhookFormData>({
        name: "",
        webhookUrl: "",
        productList: [],
        global: false,
        events: [],
        active: true,
        type: 'specific',
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [selectAll, setSelectAll] = useState(false);

    // Estados para controle de loading e dados
    const [products, setProducts] = useState<Array<{ value: string; label: string }>>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectAllProducts, setSelectAllProducts] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

    // Carregar produtos ativos ao carregar o componente
    useEffect(() => {
        fetchProducts();
    }, []);

    // Função para buscar produtos ativos
    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await axios.get(
                `${import.meta.env.VITE_API_URL}/api/products?status=active`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`,
                    },
                }
            );

            if (response.data && response.data.products) {
                const filteredProducts = response.data.products.map((product: any) => ({
                    value: product.id,
                    label: product.name,
                }));
                setProducts(filteredProducts);
            }
        } catch (error) {
            console.error("Erro ao carregar produtos:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { id, value } = e.target;

        setErrors((prev) => ({ ...prev, [id]: "" }));
        setApiError("");
        setApiSuccess("");

        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSwitchChange = (id: string, checked: boolean) => {
        setFormData((prev) => ({ ...prev, [id]: checked }));
    };

    const handleEventChange = (event: WebhookEvent, checked: boolean) => {
        setErrors((prev) => ({ ...prev, events: "" }));
        setApiError("");
        setApiSuccess("");

        setFormData((prev) => ({
            ...prev,
            events: checked
                ? [...prev.events, event]
                : prev.events.filter((e) => e !== event),
        }));
        setSelectAll(
            checked
                ? formData.events.length + 1 === [...saleEvents, ...subscriptionEvents].length
                : formData.events.length === 0
                    ? false
                    : selectAll
        ); // Atualiza selectAll com base no estado atual
    };

    const toggleSelectAll = () => {
        setErrors((prev) => ({ ...prev, events: "" }));
        setApiError("");
        setApiSuccess("");

        const allEvents = [
            ...saleEvents.map((e) => e.id),
            ...subscriptionEvents.map((e) => e.id),
        ];
        setFormData((prev) => ({
            ...prev,
            events: selectAll ? [] : allEvents,
        }));
        setSelectAll(!selectAll);
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) {
            newErrors.name = "Nome do webhook é obrigatório";
        }

        if (!formData.webhookUrl) {
            newErrors.webhookUrl = "URL do webhook é obrigatória";
        }

        if (!formData.global && !formData.productList.length) {
            newErrors.productList = "Pelo menos um produto deve ser selecionado";
        }

        if (!formData.events.length) {
            newErrors.events = "Pelo menos um evento deve ser selecionado";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            const webhookData: any = {
                name: formData.name,
                webhookUrl: formData.webhookUrl,
                productList: formData.global ? [] : formData.productList,
                global: formData.global,
                events: formData.events,
                active: formData.active,
                type: formData.global ? 'global' : 'specific',
            };

            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/company-webhook`,
                webhookData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`,
                    },
                }
            );

            if (response.data.success) {
                setApiSuccess("Webhook criado com sucesso!");
                if (onSuccess) {
                    onSuccess(response.data.data);
                }
                setTimeout(() => navigate('/apps/webhooks'), 1500);
            } else {
                setApiError(response.data.error || "Erro ao criar webhook");
                if (onError) {
                    onError(response.data.error);
                }
            }
        } catch (error: any) {
            console.error("Erro ao criar webhook:", error);
            setApiError(error.response?.data?.error || error.message || "Erro ao criar webhook");
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleProductsChange = (values: string[]) => {
        const isAllSelected = values.includes('all');

        setErrors((prev) => ({ ...prev, productList: "" }));
        setApiError("");
        setApiSuccess("");

        if (isAllSelected) {
            setSelectAllProducts(true);
            setSelectedProducts(['all']);
            setFormData((prev) => ({
                ...prev,
                global: true,
                productList: [],
                type: 'global',
            }));
        } else {
            setSelectAllProducts(false);
            setSelectedProducts(values);
            setFormData((prev) => ({
                ...prev,
                global: false,
                productList: values,
                type: 'specific',
            }));
        }
    };

    const handleGoBack = () => {
        navigate(`/apps/webhooks`);
    };

    // Lista de eventos com IDs únicos
    const saleEvents: { id: WebhookEvent; label: string }[] = [
        { id: 'sale_approved', label: 'Venda - Aprovada' },
        { id: 'sale_rejected', label: 'Venda - Recusada' },
        { id: 'sale_refunded', label: 'Venda - Reembolso' },
        { id: 'sale_chargeback', label: 'Venda - Chargeback' },
        { id: 'sale_abandoned', label: 'Venda - Abandono' },
        { id: 'sale_pix_generated', label: 'Venda - Pix gerado' },
        { id: 'sale_boleto_generated', label: 'Venda - Boleto gerado' },
    ];

    const subscriptionEvents: { id: WebhookEvent; label: string }[] = [
        { id: 'subscription_canceled', label: 'Assinatura - Cancelada' },
        { id: 'subscription_overdue', label: 'Assinatura - Atrasada' },
        { id: 'subscription_renewed', label: 'Assinatura - Renovada' },
    ];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lado esquerdo - Formulário */}
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Criar Novo Webhook">
                    {apiError && (
                        <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                            {apiError}
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                            {apiSuccess}
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar Webhook"
                                defaultChecked={true}
                                onChange={(checked) => handleSwitchChange("active", checked)}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="name">Nome do Webhook *</Label>
                            <Input
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                onChange={handleInputChange}
                                value={formData.name}
                                error={!!errors.name}
                                hint={errors.name}
                            />
                        </div>

                        <div className="mt-5">
                            <Label htmlFor="webhookUrl">URL do Webhook *</Label>
                            <Input
                                type="text"
                                id="webhookUrl"
                                placeholder="https://exemplo.com/webhook/7f7b11b9..."
                                onChange={handleInputChange}
                                value={formData.webhookUrl}
                                error={!!errors.webhookUrl}
                                hint={errors.webhookUrl}
                            />
                        </div>

                        <div className="mt-5">
                            <Label>Produtos *</Label>
                            {loadingProducts ? (
                                <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                    Carregando produtos...
                                </div>
                            ) : (
                                <div className="mt-2">
                                    <MultiSelectCheckbox
                                        options={[
                                            {
                                                id: 'all',
                                                label: 'Todos os produtos',
                                                disabled: false,
                                            },
                                            ...products.filter((product) => product.value !== 'any').map((product) => ({
                                                id: product.value,
                                                label: `${product.label}`,
                                                disabled: false,
                                                meta: product,
                                            })),
                                        ]}
                                        selectedValues={selectAllProducts ? ['all'] : selectedProducts}
                                        onChange={handleProductsChange}
                                        placeholder="Selecione os produtos"
                                        error={!!errors.productList}
                                        hint={errors.productList}
                                        searchPlaceholder="Pesquisar produtos"
                                        showSelectAll={false}
                                    />
                                    {selectAllProducts && (
                                        <p className="mt-1 text-xs text-blue-500">
                                            Este webhook será válido para todos os produtos, incluindo os futuros.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="mt-5">
                            <div className="flex flex-row align-items-center gap-4 mt-6">
                                <div className="mt-2">
                                    <Label>Selecione os eventos: *</Label>
                                </div>
                                <div className="mt-2">
                                    <span
                                        className="text-sm text-blue-500 underline cursor-pointer"
                                        onClick={toggleSelectAll}
                                    >
                                        {selectAll ? '(Desmarcar todos)' : '(Selecionar todos)'}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 mb-6">
                                <div className="space-y-3">
                                    <Label className="mb-3">Eventos de Venda</Label>
                                    {saleEvents.map((event) => (
                                        <Checkbox
                                            key={event.id}
                                            id={event.id}
                                            checked={formData.events.includes(event.id)}
                                            onChange={(checked) => handleEventChange(event.id, checked)}
                                            label={event.label}
                                        />
                                    ))}
                                </div>
                                <div className="space-y-3">
                                    <Label className="mb-3">Eventos de Assinatura</Label>
                                    {subscriptionEvents.map((event) => (
                                        <Checkbox
                                            key={event.id}
                                            id={event.id}
                                            checked={formData.events.includes(event.id)}
                                            onChange={(checked) => handleEventChange(event.id, checked)}
                                            label={event.label}
                                        />
                                    ))}
                                </div>
                            </div>
                            {errors.events && (
                                <p className="text-red-600 text-xs mt-1">{errors.events}</p>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end gap-4.5">
                            <Button
                                size="md"
                                variant="outline"
                                onClick={handleGoBack}
                            >
                                Cancelar
                            </Button>
                            <Button
                                size="md"
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : "Adicionar Webhook"}
                            </Button>
                        </div>
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}